import type {CurriedSelector, Reducer} from '@/redux/types';
import type {State} from '@/redux/typesNew';
import type {
  FetchAvailabilityError,
  FetchAvailabilityPending,
  FetchAvailabilitySuccess,
} from '@/redux/v1/availability/actionTypes';
import type {
  Availability,
  AvailabilityState,
  VacancyStatus,
} from '@/redux/v1/availability/models';
import type {ActionHandlers} from '@travel-frontend/state-handling-sdk';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {addMinutes, formatISO, isAfter, parseISO} from 'date-fns';
import {curry, fromPairs} from 'lodash';

import {FETCH_AVAILABILITY} from '@/redux/v1/availability/actionTypes';
import {
  VACANCY_STATUS_ERROR,
  VACANCY_STATUS_IDLE,
  VACANCY_STATUS_LOADING,
  VACANCY_STATUS_SOLD_OUT,
} from '@/redux/v1/availability/models';
import {availabilityState} from '@/redux/v1/v1State';
import {isVacancyStatusResolved} from '@/utils/vacancy';

export const initialState: AvailabilityState = {
  byOfferId: {},
  meta: {},
  errors: {},
  statusByOfferId: {},
};

const actionCreators: ActionHandlers<AvailabilityState> = {
  [PENDING(FETCH_AVAILABILITY)]: (
    state: AvailabilityState,
    action: FetchAvailabilityPending
  ) => ({
    ...state,
    meta: {
      ...state.meta,
      [action.meta.offerId]: action.meta,
    },
    statusByOfferId: {
      ...state.statusByOfferId,
      [action.meta.offerId]: VACANCY_STATUS_LOADING,
    },
  }),
  [SUCCESS(FETCH_AVAILABILITY)]: (
    state: AvailabilityState,
    action: FetchAvailabilitySuccess
  ) => ({
    ...state,
    byOfferId: {
      ...state.byOfferId,
      [action.meta.offerId]: action.data,
    },
    meta: {
      ...state.meta,
      [action.meta.offerId]: {
        ...state.meta[action.meta.offerId],
        fetchedAt: formatISO(new Date()),
      },
    },
    statusByOfferId: {
      ...state.statusByOfferId,
      [action.meta.offerId]: action.data?.offer
        ? action.data.status
        : VACANCY_STATUS_SOLD_OUT,
    },
  }),
  [ERROR(FETCH_AVAILABILITY)]: (
    state: AvailabilityState,
    action: FetchAvailabilityError
  ) => ({
    ...state,
    errors: {
      ...state.errors,
      [action.meta.offerId]: action.error,
    },
    meta: {
      ...state.meta,
      [action.meta.offerId]: {
        ...state.meta[action.meta.offerId],
        fetchedAt: formatISO(new Date()),
      },
    },
    statusByOfferId: {
      ...state.statusByOfferId,
      [action.meta.offerId]: VACANCY_STATUS_ERROR,
    },
  }),
};

export const availabilityReducer: Reducer<AvailabilityState> =
  createReducerUsingActionHandlers<AvailabilityState>(
    initialState,
    actionCreators
  );

export const selectVacancyStatus: CurriedSelector<string, VacancyStatus> =
  curry(
    (offerId: string, state: State) =>
      availabilityState(state).statusByOfferId[offerId] || VACANCY_STATUS_IDLE
  );

export const selectIsAvailabilityExpired: CurriedSelector<string, boolean> =
  curry((offerId: string, state: State) => {
    const isVacancyResolved = isVacancyStatusResolved(
      selectVacancyStatus(offerId, state)
    );

    if (!isVacancyResolved) {
      return false;
    }

    const {fetchedAt} = availabilityState(state).meta[offerId] || {};

    return isAfter(new Date(), addMinutes(parseISO(fetchedAt), 60));
  });

export const selectAvailabilityErrorMessage: CurriedSelector<string, string> =
  curry((offerId: string, state: State) => {
    const error = availabilityState(state).errors[offerId];
    return error?.message || '';
  });

export const selectMetaAutovacancy: CurriedSelector<
  string,
  null | undefined | boolean
> = curry(
  (offerId: string, state: State) =>
    availabilityState(state).meta[offerId]?.autoVacancy
);

export const selectAvailability: CurriedSelector<
  string,
  null | undefined | Availability
> = curry(
  (offerId: string, state: State) =>
    availabilityState(state).byOfferId[offerId]?.offer
);

export const selectVacancyStatusByOfferIds: CurriedSelector<
  Array<string>,
  Record<string, VacancyStatus>
> = curry((offerIds: Array<string>, state: State) =>
  fromPairs(
    offerIds.map((offerId) => [offerId, selectVacancyStatus(offerId, state)])
  )
);
